import { liveEventDebug } from '.'

/**
 * Scrolls the screen to the provided element/id reference to an element.
 */
export const scrollToElement = (
    idOrElement: string | HTMLElement | undefined | null,
    // By default position the top of the element halfway down as the top-nav can obscure it at times otherwise.
    screenOffset = window.innerHeight / 2,
) => {
    if (!idOrElement) {
        return
    }

    let element

    if (typeof idOrElement === 'string') {
        element = document.getElementById(idOrElement)
    } else {
        element = idOrElement
    }

    if (element) {
        const elementRect = element.getBoundingClientRect()
        const elementOffset = window.scrollY + elementRect.top

        window.scrollTo({
            left: 0,
            top: elementOffset - screenOffset,
        })
        liveEventDebug('Element found, scrolling into view: %s', idOrElement)
    }
}

// How often this checks an element is on screen.
const SCROLL_POLL_RATE = 200
/**
 * A function which aggressively scrolls to an element until it is on the screen for
 * a duration.
 * Resolves when the duration expires or the element is on screen.
 */
export const aggressiveScrollIntoView = async (
    /** The element ID to scroll to. */
    elementId: string,
    /** How long to continue checking this before the loop times out. */
    timeoutDuration = 3000,
    /** How long the element must stay on screen before this resolves. */
    screenDuration = 500,
) => {
    const element = document.getElementById(elementId)

    if (!element) return

    const startTime = Date.now()

    let firstAppearanceTime = undefined

    do {
        if (firstAppearanceTime === undefined) {
            element.scrollIntoView({
                behavior: 'smooth',
            })
        }
        await new Promise((resolve) => setTimeout(resolve, SCROLL_POLL_RATE))

        if (isVisible(element)) {
            if (firstAppearanceTime === undefined) {
                firstAppearanceTime = Date.now()
            }
        } else {
            // If it went off-screen reset the firstAppearanceTime time.
            firstAppearanceTime = undefined
        }
    } while (
        !(
            firstAppearanceTime !== undefined &&
            Date.now() - firstAppearanceTime >= screenDuration
        ) &&
        Date.now() - startTime < timeoutDuration
    )
}

const isVisible = (element: HTMLElement) => {
    const elementRect = element.getBoundingClientRect()
    const computedStyles = getComputedStyle(element)

    // Account for scroll-margins.
    const scrollMargins = {
        top: parseInt(
            computedStyles.getPropertyValue('scroll-margin-top').slice(0, -2),
        ),
        bottom: parseInt(
            computedStyles
                .getPropertyValue('scroll-margin-bottom')
                .slice(0, -2),
        ),
        left: parseInt(
            computedStyles.getPropertyValue('scroll-margin-left').slice(0, -2),
        ),
        right: parseInt(
            computedStyles.getPropertyValue('scroll-margin-right').slice(0, -2),
        ),
    }

    const canFit =
        elementRect.height + scrollMargins.top + scrollMargins.bottom <=
            window.innerHeight &&
        elementRect.width + scrollMargins.left + scrollMargins.right <=
            window.innerWidth

    let scrollRect = {
        top: elementRect.top,
        bottom: elementRect.bottom,
        left: elementRect.left,
        right: elementRect.right,
    }

    if (!canFit) {
        // Settle for an element starting at it's true top left, excluding scroll margins
        // that stretches to the bottom right corner that's visible on screen.
        // and half a screen-width from the left edge of the element,
        // inset by the scroll margin along that side.
        //
        // Border = screen
        // # = element larger than screen
        // * = Scroll margins
        // O = corners of box to consider as 'on screen'
        //
        // +------------------------+
        // |****************        |
        // |****************        |
        // |**O############O        |
        // |**##############        |
        // |**##############        |
        // |**O############O        |
        // |**##############        |
        // +------------------------+
        //  **##############
        //  ****************

        scrollRect = {
            ...scrollRect,
            bottom: Math.min(
                window.innerHeight - scrollMargins.bottom,
                scrollRect.bottom,
            ),
            right: Math.min(
                window.innerWidth - scrollMargins.right,
                scrollRect.right,
            ),
        }
    }

    // All four corners must be on screen.
    return (
        isPointInWindow(scrollRect.left, scrollRect.top) &&
        isPointInWindow(scrollRect.right, scrollRect.top) &&
        isPointInWindow(scrollRect.left, scrollRect.bottom) &&
        isPointInWindow(scrollRect.right, scrollRect.bottom)
    )
}

const isPointInWindow = (x: number, y: number) =>
    x >= 0 && x <= window.innerWidth && y >= 0 && y <= window.innerHeight
